import React, {Fragment, useEffect, useRef, useState} from 'react';
import onceVisible from "@core/scripts/helper/once-visible";
import ProductRecommendationsApi from "../../apis/product-recommendations-api.js";
import ThorRecommendationsAPI from '@personalization/scripts/apis/recommendation-api';
import Logger from '@core/scripts/helper/logger';
import Url from "@core/scripts/helper/url";
import ShopTheLook from "./ShopTheLook.jsx";
import Outfits from "./Outfits.jsx";
import ThorRecommendations from "./ThorRecommendations.jsx";
import ProductSeries from "./ProductSeries.jsx";
import SeoRecommendations from "./SeoRecommendations.jsx";
import RecommendationUtils from "./recommendation-utils.js";
import ProductMessages from "../message-list.js";
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Messages from '@core/scripts/components/messages';
import {RecommendationTypes} from "./recommendation-types.js";
import {Spinner} from "@bonprix/design-system";
import {SliderTypes} from "./slider-types.js";
import Breakpoint from "@bonprix/pattern-library/components/breakpoint";
import {ImageTypes} from "./image-types.js";

const BREAKPOINTS = [
    { minWidth: 1600, items: 6 },
    { minWidth: 1280, items: 6 },
    { minWidth: 992, items: 5 },
    { minWidth: 768, items: 4 },
    { minWidth: 480, items: 3 },
    { minWidth: 0, items: 2 }
];
const PRODUCT_CARD_GAP = 4;
const SLIDE_CUT = 1/3;

export default function ProductRecommendations({
                                                   texts,
                                                   seoRecommendations,
                                                   product,
                                                   lazyLoadingPixelDistance = 500,
                                                   fullWidth
                                               }) {
    const {recommendationPlaceholders, targetGroupLiving, currentItem: item, currentStyle, shopAssortmentId, shopId} = product;

    if (seoRecommendations) {
        return <SeoRecommendations recommendations={seoRecommendations} texts={texts}/>
    }

    const [recommendations, setRecommendations] = useState(null);
    const [productSeries, setProductSeries] = useState(null);

    const [performanceObserverEvaluated, setPerformanceObserverEvaluated] = useState(false);
    const [currentItem, setCurrentItem] = useState(item);
    const [allRecommendationsLoaded, setAllRecommendationsLoaded] = useState(false);
    const newNumberOfItemsPerPage = RecommendationUtils.getNumberOfItemsPerPage(BREAKPOINTS);
    const [numberOfItemsPerPage, setNumberOfItemsPerPage] = useState(newNumberOfItemsPerPage);
    const [carouselPadding, setCarouselPadding] = useState(RecommendationUtils.calculateCarouselPadding(newNumberOfItemsPerPage, SLIDE_CUT));

    const inViewRef = useRef(null);
    const isItemChange = useRef(false);

    const loadImmediately = useRef(false);
    const performanceObserverRegistered = useRef(false);
    const viewObserverRegistered = useRef(false);

    const boughtTogetherRef = useRef(null);
    const complementaryArticlesRef = useRef(null);
    const thorOutfitRef = useRef(null);
    const productSeriesRef = useRef(null);
    const shopTheLookRef = useRef(null);
    const outfitsRef = useRef(null);
    const componentRefs = {boughtTogetherRef, complementaryArticlesRef, thorOutfitRef, productSeriesRef, shopTheLookRef, outfitsRef};

    useEffect(() => {
        const handleResize = () => {
            const newNumberOfItemsPerPage = RecommendationUtils.getNumberOfItemsPerPage(BREAKPOINTS);
            setNumberOfItemsPerPage(newNumberOfItemsPerPage);
            setCarouselPadding(RecommendationUtils.calculateCarouselPadding(newNumberOfItemsPerPage, SLIDE_CUT));
        };

        MessageBroker.subscribe(Messages.WINDOW.RESIZED, handleResize)

        return () => {
            MessageBroker.unsubscribe(Messages.WINDOW.RESIZED, handleResize);
        };
    }, []);

    useEffect(() => {
        const subscription = MessageBroker.subscribe(ProductMessages.CHANGED, ({item}) => {
            if (item) {
                isItemChange.current = true;
                setCurrentItem(item);
            }
        });

        return () => MessageBroker.unsubscribe(subscription);
    }, []);

    useEffect(() => {
        if (isItemChange.current) {
            initialize();
        }

        return () => viewObserverRegistered.current?.unobserve(inViewRef.current);
    }, [currentItem]);

    useEffect(() => {
        if (performanceObserverEvaluated) {
            initialize();
        }
    }, [performanceObserverEvaluated]);

    useEffect(() => {
        if (allRecommendationsLoaded) {
            const recommendationsForCurrentItem = recommendations[currentItem.id];
            if (recommendationsForCurrentItem) {
                Object.keys(recommendationsForCurrentItem).forEach(key => {
                    const componentRef = componentRefs[`${key}Ref`];
                    if (recommendationsForCurrentItem[key] && componentRef?.current) {
                        const type = RecommendationUtils.getRecommendationType(key);
                        RecommendationUtils.scrollToSliderOnBrowserBack(type, componentRef.current);
                    }
                });
            }
        }
    }, [allRecommendationsLoaded]);

    if (!performanceObserverRegistered.current) {
        const observer = new PerformanceObserver((list) => {
            list.getEntries().forEach((entry) => {
                const urlParams = new URLSearchParams(window.location.search);
                if (entry.type === 'back_forward' && urlParams.get('slider') !== null) {
                    loadImmediately.current = true;
                }
            });
            setPerformanceObserverEvaluated(true);
        });
        observer.observe({type: 'navigation', buffered: true});
        performanceObserverRegistered.current = true;
    }

    const initialize = () => {
        const currentLazyLoadingPixelDistance = loadImmediately.current ? 0 : lazyLoadingPixelDistance;
        if (currentLazyLoadingPixelDistance > 0) {
            viewObserverRegistered.current = onceVisible(
                inViewRef.current,
                () => {
                    loadRecommendations();
                },
                currentLazyLoadingPixelDistance
            );
        } else {
            loadRecommendations();
        }
    };

    const getCurrentOutfitImage = (isTargetGroupLiving, shopAssortmentId, images) => {
        let imageType = getCurrentOutfitImageType(isTargetGroupLiving, shopAssortmentId);
        let image = images.find(image => image.type === imageType);

        if (!image && shopAssortmentId === 1) {
            image = images.find(image => image.type === ImageTypes.OUTFIT);
        }

        return image;
    };

    const getCurrentOutfitImageType = (isTargetGroupLiving, shopAssortmentId) => {
        if (isTargetGroupLiving) {
            return ImageTypes.AMBIENTE;
        }
        if (shopAssortmentId === 1) {
            return ImageTypes.FULL_FIGURE;
        }
        return ImageTypes.OUTFIT;
    };

    const loadRecommendations = () => {
        if (recommendations?.[currentItem.id]) {
            setAllRecommendationsLoaded(true);
            return;
        }

        setAllRecommendationsLoaded(false);
        const outfitImage = getCurrentOutfitImage(targetGroupLiving, shopAssortmentId, currentItem.images);
        if (recommendationPlaceholders.some(placeholder => placeholder.type === RecommendationTypes.SHOP_THE_LOOK) && !outfitImage?.assetId) {
            Logger.info({
                namespace: 'PDP|ShopTheLook',
                message: 'No outfit image found',
            });
        }

        const productSeriesEnabled = recommendationPlaceholders
            .some(placeholder => placeholder.type === RecommendationTypes.PRODUCT_SERIES);
        const shopTheLookEnabled = recommendationPlaceholders
            .some(placeholder => placeholder.type === RecommendationTypes.SHOP_THE_LOOK);
        const outfitsEnabled = recommendationPlaceholders
            .some(placeholder => placeholder.type === RecommendationTypes.OUTFITS);
        const presentationTypeId = recommendationPlaceholders
            .find(placeholder => placeholder.type === RecommendationTypes.OUTFITS)?.presentationTypeId || null;
        ProductRecommendationsApi
            .loadRecommendations(
                product.styleId, outfitImage?.assetId, targetGroupLiving, currentItem.id, presentationTypeId,
                productSeries !== null, productSeriesEnabled, shopTheLookEnabled, outfitsEnabled,
                fullWidth, product.shopId
            )
            .then(onDataLoaded);
    };

    const onDataLoaded = (data) => {
        if (data) {
            setRecommendations(prevRecommendations => ({
                ...prevRecommendations,
                [currentItem.id]: data
            }));
            loadThorRecommendations(data);
        }
        if (data.productSeries && !productSeries) {
            setProductSeries(data.productSeries);
        }
    };

    const getRecommendations = (type) => {
        if (fullWidth) {
            return type?.recommendations;
        }
        return type?.recommendationProducts;
    }
    const getRecommendationIds = (type) => getRecommendations(type)?.map(product => {
        if (fullWidth) {
            return product.digistyleId;
        }
        return product.productDigistyleId
    }) || [];

    const loadThorRecommendations = (data) => {
        const productSeriesIds = getRecommendationIds(data?.productSeries);
        const shopTheLookIds = getRecommendationIds(data?.shopTheLook);
        const boughtTogetherEnabled = recommendationPlaceholders
            .some(placeholder => placeholder.type === RecommendationTypes.BOUGHT_TOGETHER);
        const complementaryArticlesEnabled = recommendationPlaceholders
            .some(placeholder => placeholder.type === RecommendationTypes.COMPLEMENTARY_ARTICLES);
        const thorOutfitEnabled = recommendationPlaceholders
            .some(placeholder => placeholder.type === RecommendationTypes.THOR_OUTFIT);
        const loadThorOutfit = recommendationPlaceholders
            .some(placeholder => placeholder.type === RecommendationTypes.SHOP_THE_LOOK)
            && !getRecommendations(data?.shopTheLook);
        const excludedDigistyleIds = [...new Set(productSeriesIds.concat(shopTheLookIds))];
        const params = new URLSearchParams({
            itemNumber: currentItem.itemNumber,
            shopId: product.shopId,
            excludedDigistyleIds,
            boughtTogetherEnabled,
            complementaryArticlesEnabled,
            thorOutfitEnabled: thorOutfitEnabled && loadThorOutfit
        });

        const query = `?${params.toString()}`;
        const url = fullWidth ? '/ajax/recommendation/product-page-full-width/' : '/ajax/recommendation/product-page/';
        ThorRecommendationsAPI
            .get(Url.toAbsolute(url), query, 'Fetching Product Recommendations failed')
            .then(
            (data) => {
                setRecommendations(prevRecommendations => ({
                    ...prevRecommendations,
                    [currentItem.id]: {
                        ...prevRecommendations[currentItem.id],
                        ...data
                    }
                }));
                setAllRecommendationsLoaded(true);
            }
        );
    }

    const onRecommendationLinkClicked = (type, url) => {
        RecommendationUtils.navigateToTargetWithRecommendationTypeOrigin(type, url);
    };

    if (!recommendations || !recommendations[currentItem.id] || !allRecommendationsLoaded) {
        return <div ref={inViewRef}>
            <div className="product-recommendations__spinner-container"><Spinner edgeLength="32px"/></div>
        </div>;
    }

    const {shopTheLook, outfits, complementaryArticles, boughtTogether, thorOutfit} = recommendations[currentItem.id];
    const carousels = [];
    const showArrows = Breakpoint.keyIsIn(['large', 'xlarge', 'max']);

    if (boughtTogether) {
        carousels.push(
            <ThorRecommendations recoData={boughtTogether} onLinkClicked={onRecommendationLinkClicked}
                                 recommendationType={RecommendationTypes.BOUGHT_TOGETHER}
                                 componentRef={boughtTogetherRef} numberOfItemsPerPage={numberOfItemsPerPage}
                                 gap={PRODUCT_CARD_GAP} fullWidth={fullWidth} texts={texts}
                                 sliderType={SliderTypes.DEFAULT} carouselPadding={carouselPadding} showArrows={showArrows}/>
        );
    }

    if (productSeries) {
        const exclusionIds = getRecommendations(shopTheLook)?.map(item => item.itemId) || [];
        carousels.push(
            <ProductSeries shopId={shopId} productSeries={productSeries} exclusionIds={exclusionIds} sourceItem={currentItem}
                           sourceStyle={currentStyle} componentRef={productSeriesRef}
                           numberOfItemsPerPage={numberOfItemsPerPage} gap={PRODUCT_CARD_GAP} fullWidth={fullWidth}
                           carouselPadding={carouselPadding} showArrows={showArrows}/>
        );
    }

    if (shopTheLook) {
        carousels.push(
            <ShopTheLook shopId={shopId} shopTheLook={shopTheLook} item={item} componentRef={shopTheLookRef}
                         numberOfItemsPerPage={numberOfItemsPerPage} gap={PRODUCT_CARD_GAP} fullWidth={fullWidth}
                         texts={texts} carouselPadding={carouselPadding} showArrows={showArrows}/>
        );
    }

    if (thorOutfit) {
        carousels.push(
            <ThorRecommendations recoData={thorOutfit} onLinkClicked={onRecommendationLinkClicked}
                                 recommendationType={RecommendationTypes.THOR_OUTFIT} componentRef={thorOutfitRef}
                                 numberOfItemsPerPage={numberOfItemsPerPage} gap={PRODUCT_CARD_GAP}
                                 fullWidth={fullWidth} texts={texts} sliderType={SliderTypes.OUTFIT}
                                 carouselPadding={carouselPadding} showArrows={showArrows}/>
        );
    }

    if (complementaryArticles) {
        carousels.push(
            <ThorRecommendations recoData={complementaryArticles} onLinkClicked={onRecommendationLinkClicked}
                                 recommendationType={RecommendationTypes.COMPLEMENTARY_ARTICLES}
                                 componentRef={complementaryArticlesRef} numberOfItemsPerPage={numberOfItemsPerPage}
                                 gap={PRODUCT_CARD_GAP} fullWidth={fullWidth} texts={texts}
                                 sliderType={SliderTypes.COMPLEMENTARY_ARTICLES} carouselPadding={carouselPadding}
                                 showArrows={showArrows}/>
        );
    }

    if (outfits && outfits.length >= 2) {
        carousels.push(
            <Outfits texts={texts} shopId={shopId} itemNumber={item.itemNumber} outfits={outfits}
                     targetGroupLiving={targetGroupLiving} componentRef={outfitsRef} fullWidth={fullWidth}
                     numberOfItemsPerPage={numberOfItemsPerPage} gap={PRODUCT_CARD_GAP} showArrows={showArrows}/>
        );
    }

    return (
        <div ref={inViewRef}>
            {carousels.map((carousel, index) => (
                <Fragment key={index}>{carousel}</Fragment>
            ))}
        </div>
    );
}
